import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { siteInfo } from '@/mocks/siteInfo';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <Layout>
        <div className="py-20 text-center">
          <h2 className="text-xl font-semibold text-foreground-950">文章未找到</h2>
          <Link to="/" className="text-primary-600 mt-4 inline-block">返回首页</Link>
        </div>
      </Layout>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles.filter(
    (a) => article.relatedArticles.includes(a.slug)
  );

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://caoliu-guide.com/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://caoliu-guide.com',
      },
      ...(category
        ? [
            {
              '@type': 'ListItem',
              position: 2,
              name: category.name,
              item: `https://caoliu-guide.com/category/${category.slug}`,
            },
          ]
        : []),
      {
        '@type': 'ListItem',
        position: category ? 3 : 2,
        name: article.title,
      },
    ],
  };

  return (
    <Layout>
      <SEOHead
        title={`${article.title} - ${siteInfo.name}`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://caoliu-guide.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.image}
        schemaJsonLd={[articleSchema, breadcrumbSchema]}
      />

      <article>
        <div className="pt-24 pb-0 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  ...(category
                    ? [{ label: category.name, href: `/category/${category.slug}` }]
                    : []),
                  { label: article.title },
                ]}
              />
            </div>
          </div>
        </div>

        <div className="px-4 md:px-6 pb-10">
          <div className="max-w-4xl mx-auto">
            <div className="aspect-[21/9] rounded-2xl overflow-hidden bg-background-100 mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
                width={1200}
                height={514}
              />
            </div>

            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="text-xs font-medium text-primary-600 bg-primary-100/70 px-3 py-1 rounded-full hover:bg-primary-200/70 transition-colors whitespace-nowrap"
                >
                  {category.name}
                </Link>
              )}
              <span className="text-xs text-foreground-500 whitespace-nowrap">{article.date}</span>
              <span className="text-xs text-foreground-500 flex items-center gap-1 whitespace-nowrap">
                <i className="ri-eye-line"></i>
                {article.views.toLocaleString()} 次阅读
              </span>
              <span className="text-xs text-foreground-500 whitespace-nowrap">{article.readTime}</span>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3 leading-tight">
              {article.title}
            </h1>

            <p className="text-base text-foreground-600 mb-8 leading-relaxed">
              {article.excerpt}
            </p>

            <div className="flex items-center gap-3 mb-8">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-sm font-medium">
                C
              </div>
              <div>
                <p className="text-sm font-medium text-foreground-950">{article.author}</p>
                <p className="text-xs text-foreground-500">作者</p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 md:px-6 pb-16">
          <div className="max-w-4xl mx-auto">
            <div
              className="prose prose-base md:prose-lg max-w-none
                prose-headings:text-foreground-950
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:font-bold prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-lg md:prose-h3:text-xl prose-h3:font-semibold prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                prose-strong:text-foreground-950 prose-strong:font-semibold
                prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6
                prose-ol:my-4 prose-ol:list-decimal prose-ol:pl-6
                prose-li:text-foreground-700 prose-li:mb-1.5 prose-li:leading-relaxed
                prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline
                [&_ul]:space-y-1 [&_ol]:space-y-1"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </div>

        <div className="px-4 md:px-6 pb-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 flex-wrap pt-6 border-t border-background-200/60">
              <span className="text-xs text-foreground-500 mr-1 whitespace-nowrap">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="py-14 px-4 md:px-6 bg-background-100/60">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl font-bold text-foreground-950 mb-8">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedArticles.map((ra) => (
                <ArticleCard key={ra.slug} article={ra} />
              ))}
            </div>
          </div>
        </section>
      )}

      <div className="py-10 px-4 md:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-background-100 text-foreground-600 text-sm font-medium hover:bg-background-200/70 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    </Layout>
  );
}